import type { CancelSubject } from "shared/cancel-subject";
import type { StayAiIntervalUnit } from "@/lib/api";
import { formatPrice } from "./formatters";
import { monthlyEquivalent } from "./subscription-pricing";
import { addBillingInterval } from "./pause-dates";
import { MAX_PROPOSED_OPTIONS } from "./cancel-chat-context";
import type { SizeUpBundle } from "./cancel-chat-bundle";

/**
 * Pure copy + option builders for the cancellation chat's offer cards.
 *
 * They live outside CancelSubscriptionChat so that asserting on a STRING does
 * not require booting an AI transport and a query client. The component keeps
 * the behaviour (onClick, pending state); this file owns the words and numbers.
 */

export interface FrequencyChoice {
  /** Button text — the ACTION, e.g. "Get it every 2 months". */
  label: string;
  /** Money line, e.g. "~$52/mo · was $104/mo". Absent when it can't be computed honestly. */
  sublabel?: string;
  interval: number;
  unit: StayAiIntervalUnit;
}

/** Accepted free-form / offered cadences must land in this month range. */
export const MIN_FREQUENCY_MONTHS = 1;
export const MAX_FREQUENCY_MONTHS = 8;

/**
 * Approximate calendar months for an interval+unit. Used to enforce the 1–8
 * month cadence bound across day/week/month/year inputs.
 */
export function intervalToApproxMonths(
  interval: number,
  unit: string,
): number | null {
  if (!Number.isFinite(interval) || interval < 1) return null;
  switch (unit) {
    case "day":
      return interval / 30;
    case "week":
      return (interval * 7) / 30;
    case "month":
      return interval;
    case "year":
      return interval * 12;
    default:
      return null;
  }
}

export function isFrequencyWithinMonthBounds(
  interval: number,
  unit: string,
): boolean {
  const months = intervalToApproxMonths(interval, unit);
  return months != null && months >= MIN_FREQUENCY_MONTHS && months <= MAX_FREQUENCY_MONTHS;
}

/**
 * Normalize a requested cadence to whole months in [1, 8]. Day/week/year
 * convert via approx months then round; out-of-bounds → null.
 */
export function normalizeFrequencyToMonths(
  interval: number,
  unit: string,
): { interval: number; unit: "month" } | null {
  const months = intervalToApproxMonths(interval, unit);
  if (months == null) return null;
  const rounded = Math.round(months);
  if (rounded < MIN_FREQUENCY_MONTHS || rounded > MAX_FREQUENCY_MONTHS) {
    return null;
  }
  return { interval: rounded, unit: "month" };
}

/** True when at least one standard multiplier (2–4) yields a ≤8-month cadence. */
export function canOfferChangeFrequency(base: {
  count: number;
  unit: StayAiIntervalUnit;
} | null): boolean {
  if (!base) return false;
  for (const mult of [2, 3, 4]) {
    if (isFrequencyWithinMonthBounds(base.count * mult, base.unit)) return true;
  }
  return false;
}

export function buildFrequencyChoices(args: {
  /** Multiples of the current schedule to offer (model-authored, already clamped). */
  multipliers: number[];
  /** The current schedule, or null when it can't be read. */
  base: { count: number; unit: StayAiIntervalUnit } | null;
  subject: CancelSubject;
  contractPerDeliveryTotal: number | null;
  cadence: { interval: number; intervalUnit: string } | null;
}): FrequencyChoice[] {
  const { multipliers, base, subject, contractPerDeliveryTotal, cadence } = args;
  if (!base) return [];

  // What the WHOLE subscription costs per month today. Spacing deliveries out
  // doesn't change the price per delivery — it divides the monthly spend.
  const currentMonthly = monthlyEquivalent(contractPerDeliveryTotal, cadence);
  // contractPerDeliveryTotal can be a real `0` (every line priced to zero),
  // which is a valid number but not an honest figure to display as a saving —
  // suppress the sublabel rather than render "~$0/mo · was $0/mo".
  const hasRealMonthly = currentMonthly != null && currentMonthly > 0;

  return multipliers
    .map((mult) => {
      const interval = base.count * mult;
      const label = `Get ${subject.pronoun} every ${interval} ${base.unit}${
        interval === 1 ? "" : "s"
      }`;
      const sublabel = hasRealMonthly
        ? `~$${formatPrice(currentMonthly / mult)}/mo · was $${formatPrice(
            currentMonthly,
          )}/mo`
        : undefined;
      return { label, sublabel, interval, unit: base.unit };
    })
    .filter((c) => isFrequencyWithinMonthBounds(c.interval, c.unit));
}

/**
 * Parse a clear spoken cadence ("every 6 months", "change it for 4 months",
 * "every 8 weeks"). Returns a normalized 1–8 month cadence, or null.
 */
export function parseSpokenFrequencyMonths(
  text: string,
): { interval: number; unit: "month" } | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const patterns: Array<{ re: RegExp; unit: "month" | "week" | "day" }> = [
    {
      re: /\b(?:every|for|in)\s+(\d{1,2})\s*months?\b/i,
      unit: "month",
    },
    {
      re: /\b(\d{1,2})\s*months?\b/i,
      unit: "month",
    },
    {
      re: /\b(?:every|for|in)\s+(\d{1,2})\s*weeks?\b/i,
      unit: "week",
    },
    {
      re: /\b(\d{1,2})\s*weeks?\b/i,
      unit: "week",
    },
    {
      re: /\b(?:every|for|in)\s+(\d{1,3})\s*days?\b/i,
      unit: "day",
    },
    {
      re: /\b(\d{1,3})\s*days?\b/i,
      unit: "day",
    },
  ];
  for (const { re, unit } of patterns) {
    const m = trimmed.match(re);
    if (!m) continue;
    const n = Number(m[1]);
    if (!Number.isInteger(n) || n < 1) continue;
    const normalized = normalizeFrequencyToMonths(n, unit);
    if (normalized) return normalized;
  }
  return null;
}

export interface DelayChoice {
  /** Button text — "Delay for 3 weeks". Never "delay with". */
  label: string;
  weeks: number;
  /** The resulting next-billing date, YYYY-MM-DD. */
  date: string;
}

/** A lone delay option is an ultimatum, not a choice. Always offer at least two. */
export const MIN_DELAY_OPTIONS = 2;

const delayLabel = (weeks: number) =>
  `Delay for ${weeks} week${weeks === 1 ? "" : "s"}`;

/**
 * Parse a clear spoken delay length from typed text ("okay, delay for 3 weeks
 * please"). Returns 1–12 or null. Used as a widget safety net so we commit via
 * accept path instead of waiting for the model to re-offer.
 */
export function parseSpokenDelayWeeks(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const patterns = [
    /\bdelay(?:\s+(?:for|it|my(?:\s+next)?(?:\s+(?:delivery|shipment|order))?))?(?:\s+for)?\s+(\d{1,2})\s*weeks?\b/i,
    /\bfor\s+(\d{1,2})\s*weeks?\b/i,
    /\b(\d{1,2})\s*weeks?\b/i,
  ];
  for (const re of patterns) {
    const m = trimmed.match(re);
    if (!m) continue;
    const n = Number(m[1]);
    if (Number.isInteger(n) && n >= 1 && n <= 12) return n;
  }
  return null;
}

export function buildDelayChoices(args: {
  /** Model-authored weeks (already clamped), or null to use the presets. */
  weeks: number[] | null;
  presets: { weeks: number; date: string }[];
  nextBillingDate: string | null;
}): DelayChoice[] {
  const { weeks, presets, nextBillingDate } = args;
  // The model's set can only be honoured when we have a date to compute from;
  // without one, fall back to the presets (which already carry their dates).
  const chosen =
    weeks && nextBillingDate
      ? weeks.map((w) => ({
        weeks: w,
        date: addBillingInterval(nextBillingDate, "week", w),
      }))
      : presets;

  // NEVER present a lone delay. A single option is an ultimatum, not a choice —
  // "Delay for 8 weeks" or nothing, on a screen the customer is already trying
  // to leave. A live conversation did exactly that. So when the model names
  // fewer than two, top up from the standard presets: its own pick still leads
  // (it is the one tailored to what they said), the presets fill in behind it,
  // and duplicates are dropped. If there genuinely isn't a second distinct
  // option to offer, one real choice beats a fabricated one.
  const merged = [...chosen];
  for (const p of presets) {
    if (merged.length >= MIN_DELAY_OPTIONS) break;
    if (!merged.some((c) => c.weeks === p.weeks)) merged.push(p);
  }

  return merged
    .slice(0, MAX_PROPOSED_OPTIONS)
    .map((p) => ({ label: delayLabel(p.weeks), weeks: p.weeks, date: p.date }));
}

/**
 * Deterministic copy + numbers for the size-up bundle card.
 *
 * The model's tool call states NO figures (same contract as delay/swap): this card
 * owns every number — current $/mo, new $/mo, the saving, the new size + cadence,
 * and the "same amount each month" reassurance that the servings-per-month is
 * unchanged. All are derived from the pre-computed SizeUpBundle, never re-derived
 * here, so the card can't drift from the eligibility math that produced the offer.
 */
export interface BundleCardCopy {
  /**
   * The invitation shown ABOVE the card: names packs **of servings**, the longer
   * cadence, and the saving PER MONTH — e.g. "Right now you're getting 1 pack of
   * 32 servings a month. How about we switch you to 1 pack of 64 servings every
   * 2 months instead? You'll save $10/mo." Deterministic (not model-authored).
   * Size-up review can omit the trailing saving via {@link BuildBundleCardCopyOptions}.
   */
  inviteLine: string;
  /** Headline, e.g. "Save $12/mo". */
  savingLine: string;
  /** e.g. "$44/mo now". */
  currentMonthlyLabel: string;
  /** e.g. "$32/mo now". */
  newMonthlyLabel: string;
  /** e.g. "1 pack of 64 servings, every 2 months". */
  planLine: string;
  /** Reassurance the amount they get each month doesn't change. */
  sameAmountLine: string;
  /** Accept-button text, e.g. "Switch to 64 servings" or "Switch to 3-pack". */
  acceptLabel: string;
}

export interface BuildBundleCardCopyOptions {
  /**
   * When true, invite ends at "instead?" — used by SizeUpReviewDialog where the
   * summary box already shows the saving. Cancel chat keeps the full invite.
   */
  omitInviteSaving?: boolean;
}

const cadenceWords = (months: number): string =>
  months === 1 ? "every month" : `every ${months} months`;

/** Packs for display: whole when round, else 1dp (e.g. 0.5 packs/month). */
const formatPacks = (n: number): string => {
  const rounded = Number.isInteger(n) ? n : Math.round(n * 10) / 10;
  const label = Number.isInteger(rounded)
    ? String(rounded)
    : String(rounded);
  return `${label} pack${rounded === 1 ? "" : "s"}`;
};

/** "1 pack of 32 servings" — pack count plus advertised servings on that SKU. */
const formatPackOfServings = (packs: number, servingsPerPack: number): string => {
  const servings = Number.isInteger(servingsPerPack)
    ? String(servingsPerPack)
    : String(Math.round(servingsPerPack * 10) / 10);
  return `${formatPacks(packs)} of ${servings} servings`;
};

export function buildBundleCardCopy(
  bundle: SizeUpBundle,
  currencySymbol = "$",
  options: BuildBundleCardCopyOptions = {},
): BundleCardCopy {
  // Quote the saving PER MONTH so the invite matches the "You save" figure in
  // the box (shipping folded into savingPerMonth when known). Name pack +
  // servings so 32→64 isn't mistaken for "same pack, longer wait".
  const cadence = cadenceWords(bundle.newIntervalMonths);
  const shippingHint = bundle.shippingIncluded
    ? " Prices include shipping."
    : "";
  const currentServingsPerPack =
    bundle.currentPacksPerMonth > 0
      ? bundle.servingsPerMonth / bundle.currentPacksPerMonth
      : bundle.servingsPerMonth;
  const newServingsPerPack =
    bundle.newPackCount > 0
      ? bundle.newServings / bundle.newPackCount
      : bundle.newServings;
  const currentPlan = formatPackOfServings(
    bundle.currentPacksPerMonth,
    currentServingsPerPack,
  );
  const newPlan = formatPackOfServings(bundle.newPackCount, newServingsPerPack);
  // Single-pack upsizes name the serving size; multi-packs name the pack count
  // ("Switch to 3-pack") — the SKU language customers already see on Rose Nectar.
  const acceptLabel =
    bundle.newPackCount > 1
      ? `Switch to ${bundle.newPackCount}-pack`
      : `Switch to ${Number.isInteger(newServingsPerPack) ? newServingsPerPack : Math.round(newServingsPerPack)} servings`;
  const inviteBase = `Right now you're getting ${currentPlan} a month. How about we switch you to ${newPlan} ${cadence} instead?`;
  const inviteLine = options.omitInviteSaving
    ? inviteBase
    : `${inviteBase} You'll save ${currencySymbol}${formatPrice(
        bundle.savingPerMonth,
      )}/mo.${shippingHint}`;
  return {
    inviteLine,
    savingLine: `Save ${currencySymbol}${formatPrice(
      bundle.savingPerMonth,
    )}/mo`,
    currentMonthlyLabel: `${currencySymbol}${formatPrice(
      bundle.currentMonthly,
    )}/mo now`,
    newMonthlyLabel: `${currencySymbol}${formatPrice(bundle.newMonthly)}/mo`,
    planLine: `${newPlan}, ${cadenceWords(bundle.newIntervalMonths)}`,
    sameAmountLine:
      "You get the same amount each month — it just arrives less often.",
    acceptLabel,
  };
}

/**
 * Manage-tab banner copy for the size-up offer.
 * Title uses the computed monthly saving from the offer (same figure as the
 * review dialog), e.g. "Save $8.48 by getting your product every 2 months".
 * `{amount}` is a placeholder so the banner can bold the real figure.
 */
export function buildSizeUpBannerCopy(
  bundle: SizeUpBundle,
  currencySymbol = "$",
): {
  titleTemplate: string;
  body: string;
  acceptLabel: string;
  savingsAmount: string;
} {
  const { acceptLabel } = buildBundleCardCopy(bundle, currencySymbol);
  const cadence = cadenceWords(bundle.newIntervalMonths);
  return {
    titleTemplate: `Save {amount} by getting your product ${cadence}`,
    body: `${acceptLabel} ${cadence} and enjoy extra savings`,
    acceptLabel,
    savingsAmount: `${currencySymbol}${formatPrice(bundle.savingPerMonth)}`,
  };
}

/** Fixed body copy for the switch-to-one-time card (matches BeforeYouCancelBody). */
export const SWITCH_TO_ONE_TIME_BODY =
  "Subscription prices are designed for customers with at least 2 deliveries. Switching your order to One-time updates the price to match.";

/** Fixed footer copy for the switch-to-one-time card (matches BeforeYouCancelBody). */
export const SWITCH_TO_ONE_TIME_FOOTER =
  "You can skip, pause or cancel in seconds - anytime.";

/**
 * First presentation of the switch card (matches BeforeYouCancelBody).
 * Soft retention framing — not yet the "cancel via one-time" exit.
 */
export const SWITCH_TO_ONE_TIME_FIRST_TITLE =
  "Got it - we can switch your order to a one-time purchase.";

/**
 * Re-offer / exit framing — used on the 2nd+ presentation after they insist on
 * cancelling. Widget-owned so the model cannot invent "Cancel isn't available yet…".
 */
export const SWITCH_TO_ONE_TIME_TITLE =
  "To cancel, switch your order to a one-time purchase.";

/** Alias kept for call sites that previously took an optional product title. */
export function switchToOneTimeDefaultTitle(_productTitle?: string): string {
  return SWITCH_TO_ONE_TIME_FIRST_TITLE;
}
